import React, { useState } from 'react';
import { Box, Typography, Button, MenuItem, Menu, IconButton, TablePagination } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { API, graphqlOperation } from 'aws-amplify';
import { deletePart, updateInvoice } from '../../graphql/mutations';

const PartsList = ({ 
  currentInvoice, 
  getSortedAndFilteredParts,
  getPaginatedParts,
  sortOption,
  setSortOption,
  filterOption,
  setFilterOption,
  fetchInvoiceDetails,
  pricelistMap = {}, // Default to empty object if not provided
  aggregateParts = false,
  showFullPartNumbers = true, // New prop for showing/hiding full part numbers
  handleIncrementPart = null,
  handleDecrementPart = null,
  updating = false,
  // Pagination props
  page = 0,
  rowsPerPage = 25,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  totalCount = 0
}) => {
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Get the filtered and sorted parts
  // Exclude aftermarket and empty parts from the main catalytic list
  const allCatalyticParts = getSortedAndFilteredParts(
    (currentInvoice?.parts?.items || []).filter(part => 
      part.status !== 'unit_by_weight' &&
      part.status !== 'aftermarket' &&
      part.status !== 'empty' &&
      String(part.partNumber || '').trim().toLowerCase() !== 'after market'
    )
  );
  
  const allNonCatalyticsParts = getSortedAndFilteredParts(
    (currentInvoice?.parts?.items || []).filter(part => 
      part.status === 'unit_by_weight'
    )
  );

  // Empties and Aftermarkets sections
  const allEmptiesParts = getSortedAndFilteredParts(
    (currentInvoice?.parts?.items || []).filter(part => part.status === 'empty')
  );
  const allAftermarketParts = getSortedAndFilteredParts(
    (currentInvoice?.parts?.items || []).filter(part => 
      part.status === 'aftermarket' || (String(part.partNumber || '').trim().toLowerCase() === 'after market')
    )
  );
  
  // Get paginated parts if getPaginatedParts is provided, otherwise use all parts
  const catalyticParts = getPaginatedParts ? 
    getPaginatedParts(
      (currentInvoice?.parts?.items || []).filter(part => 
        part.status !== 'unit_by_weight' &&
        part.status !== 'aftermarket' &&
        part.status !== 'empty' &&
        String(part.partNumber || '').trim().toLowerCase() !== 'after market'
      )
    ) :
    allCatalyticParts;
  
  const nonCatalyticsParts = getPaginatedParts ? 
    getPaginatedParts(currentInvoice?.parts?.items?.filter(part => part.status === 'unit_by_weight') || []) :
    allNonCatalyticsParts;

  const emptiesParts = allEmptiesParts; // Typically few; no pagination necessary
  const aftermarketParts = allAftermarketParts;

  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };
  
  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };
  
  const handleSortChange = (option) => {
    setSortOption(option);
    handleSortMenuClose();
  };
  
  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };
  
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };
  
  const handleFilterChange = (option) => {
    setFilterOption(option);
    handleFilterMenuClose();
  };

  const handleDeletePart = async (partId) => {
    try {
      await API.graphql(
        graphqlOperation(deletePart, { input: { id: partId } })
      );
      await API.graphql(
        graphqlOperation(updateInvoice, {
          input: { id: currentInvoice.id, partCount: (currentInvoice.partCount || 1) - 1 }
        })
      );
      await fetchInvoiceDetails(currentInvoice.id);
    } catch (error) {
      console.error('Error deleting part from invoice:', error);
    }
  };

  // Helper function to get pricelist name
  const getPricelistName = (pricelistId) => {
    if (!pricelistId) return "No pricelist";
    return pricelistMap[pricelistId] || `Pricelist ID: ${pricelistId.substring(0, 8)}...`;
  };

  // Helper function to mask part numbers if needed
  const getPartNumberDisplay = (partNumber) => {
    if (showFullPartNumbers) return partNumber;
    
    // Find the index of the first space
    const spaceIndex = partNumber.indexOf(' ');
    
    // If there's no space, return the first few characters and then mask the rest
    if (spaceIndex === -1) {
      const visiblePart = partNumber.substring(0, Math.min(3, partNumber.length));
      return partNumber.length > 3 ? `${visiblePart} XXXX` : partNumber;
    }
    
    // Otherwise, show everything before the first space and mask the rest
    return `${partNumber.substring(0, spaceIndex)} XXXX`;
  };

  if (!currentInvoice?.parts?.items || currentInvoice.parts.items.length === 0) {
    return (
      <Box sx={{ 
        border: '1px solid #24e4ff',
        borderRadius: '10px',
        padding: '20px',
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        <Typography sx={{ color: '#24e4ff' }}>
          No parts added to this invoice yet.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {catalyticParts.length > 0 && (
        <Box sx={{ 
          marginBottom: '20px',
          border: '1px solid #24e4ff',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '10px 15px',
            borderBottom: '1px solid #24e4ff'
          }}>
            <Typography variant="h6" sx={{ color: '#24e4ff' }}>
              Catalytic Converters
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                startIcon={<FilterListIcon />}
                onClick={handleFilterMenuOpen}
                variant="outlined"
                size="small"
                sx={{ 
                  color: '#24e4ff', 
                  borderColor: '#24e4ff',
                  '&:hover': {
                    borderColor: '#35d33d',
                    color: '#35d33d'
                  }
                }}
              >
                Filter
              </Button>
              <Menu
                anchorEl={filterMenuAnchor}
                open={Boolean(filterMenuAnchor)}
                onClose={handleFilterMenuClose}
              >
                <MenuItem 
                  onClick={() => handleFilterChange('all')}
                  selected={filterOption === 'all'}
                >
                  All Units
                </MenuItem>
                <MenuItem 
                  onClick={() => handleFilterChange('wholeUnits')}
                  selected={filterOption === 'wholeUnits'}
                >
                  Whole Units
                </MenuItem>
                <MenuItem 
                  onClick={() => handleFilterChange('halfUnits')}
                  selected={filterOption === 'halfUnits'}
                >
                  Half Units
                </MenuItem>
              </Menu>
              
              <Button 
                startIcon={<SortIcon />}
                onClick={handleSortMenuOpen}
                variant="outlined"
                size="small"
                sx={{ 
                  color: '#24e4ff', 
                  borderColor: '#24e4ff',
                  '&:hover': {
                    borderColor: '#35d33d',
                    color: '#35d33d'
                  }
                }}
              >
                Sort
              </Button>
              <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={handleSortMenuClose}
              >
                <MenuItem 
                  onClick={() => handleSortChange('newest')}
                  selected={sortOption === 'newest'}
                >
                  Newest First
                </MenuItem>
                <MenuItem 
                  onClick={() => handleSortChange('oldest')}
                  selected={sortOption === 'oldest'}
                >
                  Oldest First
                </MenuItem>
                <MenuItem 
                  onClick={() => handleSortChange('partNumber')}
                  selected={sortOption === 'partNumber'}
                >
                  Part Number
                </MenuItem>
                <MenuItem 
                  onClick={() => handleSortChange('priceHighToLow')}
                  selected={sortOption === 'priceHighToLow'}
                >
                  Price (High to Low)
                </MenuItem>
                <MenuItem 
                  onClick={() => handleSortChange('priceLowToHigh')}
                  selected={sortOption === 'priceLowToHigh'}
                >
                  Price (Low to High)
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          {catalyticParts.map((part, index) => (
            <Box 
              key={part.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px',
                borderBottom: index < catalyticParts.length - 1 ? '1px solid #24e4ff' : 'none',
                backgroundColor: '#000',
                position: 'relative',
              }}
            >
              <Box>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px' }}>
                  {getPartNumberDisplay(part.partNumber)}
                  {aggregateParts && part.quantity > 1 && (
                    <Typography component="span" sx={{ color: '#24e4ff', ml: 1, fontWeight: 'bold' }}>
                      (×{part.quantity})
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ color: '#24e4ff', fontSize: '14px', opacity: 0.8 }}>
                    Grade: {part.grade === 1 ? 'Full' : 'Half'}
                  </Typography>
                  {part.createdAt && (
                    <Typography sx={{ color: '#24e4ff', fontSize: '12px', opacity: 0.7 }}>
                      {new Date(part.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography sx={{ color: '#24e4ff', fontSize: '12px', opacity: 0.7 }}>
                    From pricelist: {getPricelistName(part.fromPricelist)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px', marginRight: '8px' }}>
                  {aggregateParts && part.quantity > 1 
                    ? `$${part.totalPrice.toFixed(2)} (${part.quantity} × $${parseFloat(part.price || 0).toFixed(2)})` 
                    : `$${parseFloat(part.price || 0).toFixed(2)}`}
                </Typography>
                {!aggregateParts && handleIncrementPart && handleDecrementPart && (
                  <Box sx={{ display: 'flex', mr: 2 }}>
                    <IconButton 
                      size="small" 
                      disabled={updating}
                      onClick={() => handleDecrementPart(part)}
                      sx={{ color: '#ff4d4f' }}
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                    <IconButton 
                      size="small"
                      disabled={updating}
                      onClick={() => handleIncrementPart(part)}
                      sx={{ color: '#24e4ff' }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Box>
                )}
                <IconButton onClick={() => handleDeletePart(part.id)}>
                  <DeleteIcon sx={{ color: '#24e4ff' }} />
                </IconButton>
              </Box>
            </Box>
          ))}
          {totalCount > rowsPerPage && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              rowsPerPageOptions={[25, 50, 100]}
              sx={{
                color: '#24e4ff',
                '& .MuiTablePagination-selectIcon': {
                  color: '#24e4ff',
                },
                '& .MuiTablePagination-select': {
                  color: '#24e4ff',
                },
                '& .MuiTablePagination-selectLabel': {
                  color: '#24e4ff',
                },
                '& .MuiTablePagination-displayedRows': {
                  color: '#24e4ff',
                },
                '& .MuiIconButton-root': {
                  color: '#24e4ff',
                },
              }}
            />
          )}
        </Box>
      )}

      {nonCatalyticsParts.length > 0 && (
        <Box sx={{ 
          marginBottom: '20px',
          border: '1px solid #24e4ff',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '10px 15px',
            borderBottom: '1px solid #24e4ff'
          }}>
            <Typography variant="h6" sx={{ color: '#24e4ff' }}>
              Other Materials
            </Typography>
          </Box>
          {nonCatalyticsParts.map((part, index) => (
            <Box 
              key={part.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px',
                borderBottom: index < nonCatalyticsParts.length - 1 ? '1px solid #24e4ff' : 'none',
                backgroundColor: '#000',
              }}
            >
              <Box>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px' }}>
                  {getPartNumberDisplay(part.partNumber)}
                  {aggregateParts && part.quantity > 1 && (
                    <Typography component="span" sx={{ color: '#24e4ff', ml: 1, fontWeight: 'bold' }}>
                      (×{part.quantity})
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {part.cost && (
                    <Typography sx={{ color: '#24e4ff', fontSize: '14px', opacity: 0.8 }}>
                      Weight: {part.cost}
                    </Typography>
                  )}
                  {part.createdAt && (
                    <Typography sx={{ color: '#24e4ff', fontSize: '12px', opacity: 0.7 }}>
                      {new Date(part.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px', marginRight: '8px' }}>
                  {aggregateParts && part.quantity > 1 
                    ? `$${part.totalPrice.toFixed(2)} (${part.quantity} × $${parseFloat(part.price || 0).toFixed(2)})` 
                    : `$${parseFloat(part.price || 0).toFixed(2)}`}
                </Typography>
                {!aggregateParts && handleIncrementPart && handleDecrementPart && (
                  <Box sx={{ display: 'flex', mr: 2 }}>
                    <IconButton 
                      size="small" 
                      disabled={updating}
                      onClick={() => handleDecrementPart(part)}
                      sx={{ color: '#ff4d4f' }}
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                    <IconButton 
                      size="small"
                      disabled={updating}
                      onClick={() => handleIncrementPart(part)}
                      sx={{ color: '#24e4ff' }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Box>
                )}
                <IconButton onClick={() => handleDeletePart(part.id)}>
                  <DeleteIcon sx={{ color: '#24e4ff' }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {aftermarketParts.length > 0 && (
        <Box sx={{ 
          marginBottom: '20px',
          border: '1px solid #24e4ff',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '10px 15px',
            borderBottom: '1px solid #24e4ff'
          }}>
            <Typography variant="h6" sx={{ color: '#24e4ff' }}>
              Aftermarkets
            </Typography>
          </Box>
          {aftermarketParts.map((part, index) => (
            <Box 
              key={part.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px',
                borderBottom: index < aftermarketParts.length - 1 ? '1px solid #24e4ff' : 'none',
                backgroundColor: '#000',
              }}
            >
              <Box>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px' }}>
                  {getPartNumberDisplay(part.partNumber)}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {part.createdAt && (
                    <Typography sx={{ color: '#24e4ff', fontSize: '12px', opacity: 0.7 }}>
                      {new Date(part.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px', marginRight: '8px' }}>
                  ${parseFloat(part.price || 0).toFixed(2)}
                </Typography>
                {handleIncrementPart && handleDecrementPart && (
                  <Box sx={{ display: 'flex', mr: 2 }}>
                    <IconButton 
                      size="small" 
                      disabled={updating}
                      onClick={() => handleDecrementPart(part)}
                      sx={{ color: '#ff4d4f' }}
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                    <IconButton 
                      size="small"
                      disabled={updating}
                      onClick={() => handleIncrementPart(part)}
                      sx={{ color: '#24e4ff' }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Box>
                )}
                <IconButton onClick={() => handleDeletePart(part.id)}>
                  <DeleteIcon sx={{ color: '#24e4ff' }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {emptiesParts.length > 0 && (
        <Box sx={{ 
          marginBottom: '20px',
          border: '1px solid #24e4ff',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '10px 15px',
            borderBottom: '1px solid #24e4ff'
          }}>
            <Typography variant="h6" sx={{ color: '#24e4ff' }}>
              Empties
            </Typography>
          </Box>
          {emptiesParts.map((part, index) => (
            <Box 
              key={part.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '15px',
                borderBottom: index < emptiesParts.length - 1 ? '1px solid #24e4ff' : 'none',
                backgroundColor: '#000',
              }}
            >
              <Box>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px' }}>
                  {getPartNumberDisplay(part.partNumber)}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {part.createdAt && (
                    <Typography sx={{ color: '#24e4ff', fontSize: '12px', opacity: 0.7 }}>
                      {new Date(part.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ color: '#24e4ff', fontSize: '16px', marginRight: '8px' }}>
                  $0.00
                </Typography>
                {handleIncrementPart && handleDecrementPart && (
                  <Box sx={{ display: 'flex', mr: 2 }}>
                    <IconButton 
                      size="small" 
                      disabled={updating}
                      onClick={() => handleDecrementPart(part)}
                      sx={{ color: '#ff4d4f' }}
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                    <IconButton 
                      size="small"
                      disabled={updating}
                      onClick={() => handleIncrementPart(part)}
                      sx={{ color: '#24e4ff' }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Box>
                )}
                <IconButton onClick={() => handleDeletePart(part.id)}>
                  <DeleteIcon sx={{ color: '#24e4ff' }} />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

export default PartsList; 